import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { CustomBatteryChart } from '@/features/charts/components/chart-types/battery-chart-components/custom-battery-chart';
import { AggregationType } from '@/features/charts/components/create-chart-type-selection';
import { DashboardProjectCardHeaderContent } from '@/features/dashboard/components/dashboard/dashboard-project-card-header-content';
import { BaseProjectSlideCardProps } from '@/features/dashboard/components/dashboard/dashboard-project-slot-card';
import { getKpiMetricsSummary } from '@/features/dashboard/utils/kpi-metrics';
import { useKpiTranslations } from '@/features/projects/project-details/components/kpi-card';
import { getKpiUniqueId } from '@/features/projects/shared/utils/kpi-utils';
import TendencyIndicator, {
  getTendencyFromPercentageChange,
} from '@/shared/components/tendency-indicator';
import { Card, CardContent, CardHeader } from '@/shared/components/ui/card';
import { ValueSign } from '@/shared/components/value-sign';
import { getKpiUnitAbbreviationTranslation } from '@/shared/hooks/use-kpi-unit-translation';
import { formatDashboardNumber, formatPercentage } from '@/shared/utils/format';

export const DashboardProjectBattery = ({
  project,
  kpi,
  currentCardIndex,
  onCardIndexChange,
  isPaused,
  onTogglePause,
}: BaseProjectSlideCardProps) => {
  const { tKpiTypes } = useKpiTranslations();
  const navigate = useNavigate();

  const { currentValue, previousValue, changePercentage } =
    getKpiMetricsSummary(kpi?.metrics);

  const handleCardClick = () => {
    if (kpi) {
      navigate(
        paths.insights.getHref({
          kpiUniqueId: getKpiUniqueId(kpi),
          projectId: project.id.toString(),
        }),
      );
    }
  };

  return (
    <Card variant="informationAlt" className="size-full">
      <CardHeader className="flex flex-row items-start justify-between">
        <DashboardProjectCardHeaderContent
          kpiName={kpi?.name}
          projectName={project.name}
          currentPageIndex={currentCardIndex}
          onCardIndexChange={onCardIndexChange}
          isPaused={isPaused}
          onTogglePause={onTogglePause}
        />
      </CardHeader>
      <CardContent className="flex flex-1 flex-col">
        <div className="flex flex-1 flex-col justify-center">
          {kpi && (
            <CustomBatteryChart
              chartResult={{
                chartConfig: {
                  [kpi.name]: {
                    label: kpi.name,
                  },
                },
                chartData: [
                  {
                    aggregationType: AggregationType.ACCUMULATED,
                    name: kpi.name,
                    value: currentValue,
                    kpiName: kpi.name,
                    kpiId: kpi.id,
                    unit: kpi.unit,
                    budget: kpi.max_value,
                    lastEntryValue: previousValue,
                    date: new Date(
                      kpi.metrics?.last_entry_date ?? '',
                    ).getTime(),
                  },
                ],
              }}
              maxValue={kpi.max_value ?? 0}
              chartSettings={{
                hideXAxis: true,
                hideYAxis: true,
                hideGrid: true,
                height: 'h-[60%]',
              }}
            />
          )}
        </div>
        <div
          className="flex cursor-pointer flex-row justify-between"
          onClick={handleCardClick}
        >
          <span className="whitespace-nowrap text-2xl font-semibold leading-none">
            {formatDashboardNumber(currentValue)}{' '}
            {getKpiUnitAbbreviationTranslation(tKpiTypes, kpi?.unit ?? null)}
          </span>
          <div className="flex flex-row items-center gap-3">
            <TendencyIndicator
              tendency={getTendencyFromPercentageChange(changePercentage)}
              size="default"
            />
            <span className="text-base font-semibold">
              <ValueSign value={changePercentage} showPositiveSign={true} />
              {formatPercentage(changePercentage)}%
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
